import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useAuthContext } from './AuthProvider';
import { useForgeDoc } from '@/hooks/forge';
import { getSpacePath, getMembersPath } from '@/lib/spaces';
import type { Space, SpaceMember } from '@/types/models';
import type { Role } from '@/types/roles';

/* ─── Context shape ─── */
interface SpaceContextValue {
  /** The currently active workspace, or null if none selected. */
  activeSpace: Space | null;
  /** The current user's role in the active space, or null. */
  activeSpaceRole: Role | null;
  /** Set the active workspace by ID. Persists to localStorage. */
  setActiveSpaceId: (id: string | null) => void;
  /** True while space or member data is being loaded. */
  loading: boolean;
}

const SpaceContext = createContext<SpaceContextValue | undefined>(undefined);

const STORAGE_KEY = 'ezhaul:activeSpaceId';

/* ─── Provider ─── */
interface SpaceProviderProps {
  children: ReactNode;
}

/**
 * Provides the active space and the current user's role within it.
 * Listens to both the space document and the user's member document.
 * Persists the active space ID in localStorage.
 */
export function SpaceProvider({ children }: SpaceProviderProps) {
  const { user } = useAuthContext();
  const [activeSpaceId, setActiveSpaceIdState] = useState<string | null>(
    () => localStorage.getItem(STORAGE_KEY),
  );

  const setActiveSpaceId = useCallback((id: string | null) => {
    setActiveSpaceIdState(id);
    if (id) {
      localStorage.setItem(STORAGE_KEY, id);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const spacePath = activeSpaceId ? getSpacePath(activeSpaceId) : null;
  const { data: activeSpace, loading: spaceLoading } = useForgeDoc<Space>(spacePath);

  const memberPath = (activeSpaceId && user?.id) ? `${getMembersPath(activeSpaceId)}/${user.id}` : null;
  const { data: activeSpaceMember } = useForgeDoc<SpaceMember>(memberPath);
  
  const activeSpaceRole = activeSpaceMember?.role ?? null;
  const loading = spaceLoading && activeSpaceId !== null;

  // Cleanup local storage if space doesn't exist anymore and not loading
  useEffect(() => {
    if (activeSpaceId && !loading && !activeSpace) {
      setActiveSpaceId(null);
    }
  }, [activeSpaceId, loading, activeSpace, setActiveSpaceId]);

  const value = useMemo<SpaceContextValue>(
    () => ({
      activeSpace,
      activeSpaceRole,
      setActiveSpaceId,
      loading,
    }),
    [activeSpace, activeSpaceRole, setActiveSpaceId, loading],
  );

  return (
    <SpaceContext.Provider value={value}>
      {children}
    </SpaceContext.Provider>
  );
}

/* ─── Hook ─── */
/**
 * Returns the active space context.
 * Must be used within a SpaceProvider.
 */
export function useSpaceContext(): SpaceContextValue {
  const ctx = useContext(SpaceContext);
  if (ctx === undefined) {
    throw new Error('useSpaceContext must be used within a SpaceProvider');
  }
  return ctx;
}
